import { ChangeEventHandler, useState } from "react";
import produce from "immer";
import { useTranslation } from "react-i18next";
import { Box, TextField, Typography } from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import AutocompleteSearchInput from "components/AutocompleteSearchInput";
import { isBlank } from "utils/string";
import { ErrorMessage } from "../../Actions/Styled";
import { Field, FormFields } from "../../../../common/FormFields";

import { useRuleContext } from "../../../context/ruleContext";

import { SenderNotification } from "@dashboard-v3/api";
import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";
import useNotificationVariables from "pages/Rules/hooks/useNotificationVariables";
import { getApprovalSteps } from "pages/Rules/Form/context/ruleSelectors";

export default function SenderSettings() {
  const { variables, replaceIdsWithLabels, replaceLabelsWithIds } =
    useNotificationVariables({
      ruleType: "REQUEST_APPROVAL",
      notificationType: "SENDER",
    });
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const { senderNotification } = getApprovalSteps(state.rule);
  const [isEnabled, setIsEnabled] = useState(Boolean(senderNotification));
  const recipient = t("approvalSteps.recipientType.sender");
  const titleError = isBlank(senderNotification?.title);
  const titleMaxLength =
    senderNotification?.title?.length > NOTIFICATION_LIMITS.TITLE;
  const bodyError = isBlank(senderNotification?.bodyText);
  const bodyMaxLength =
    senderNotification?.bodyText?.length > NOTIFICATION_LIMITS.BODY_TEXT;
  const accessLinkError = isBlank(senderNotification?.accessLinkLabel);
  const cancelLinkError = isBlank(senderNotification?.cancelLinkLabel);
  const linkLabelDefaults = {
    accessLinkLabel: t("approvalSteps.senderEmail.access"),
    cancelLinkLabel: t("approvalSteps.senderEmail.cancel"),
  };

  const updateSettings = (update: Partial<SenderNotification>) => {
    dispatch({
      type: "updateStep",
      payload: {
        step: "approvalSteps",
        changes: { senderNotification: update },
      },
    });
  };

  const handleNotificationText: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, value } = e.target;
    const update = produce(senderNotification, draft => {
      draft[name] = value;
    });

    updateSettings(update);
  };

  const handleNotificationLinks: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, checked } = e.target;
    const update = produce(senderNotification, draft => {
      const linkLabelKey = `${name}Label`;

      draft[name] = checked;

      if (checked && !draft[linkLabelKey]) {
        draft[linkLabelKey] = linkLabelDefaults[linkLabelKey];
      }

      if (!checked) {
        draft[linkLabelKey] = "";
      }
    });

    updateSettings(update);
  };

  function handleNotificationBody(value: string) {
    const update = produce(senderNotification, draft => {
      draft.bodyText = replaceLabelsWithIds(value);
    });

    updateSettings(update);
  }

  const onEnableNotification: ChangeEventHandler<HTMLInputElement> = e => {
    const { checked } = e.target;
    setIsEnabled(checked);

    if (!checked) return updateSettings(null);

    updateSettings({
      accessLink: false,
      accessLinkLabel: t("approvalSteps.senderEmail.access"),
      bodyText: t("approvalSteps.senderEmail.body"),
      cancelLink: true,
      cancelLinkLabel: t("approvalSteps.senderEmail.cancel"),
      title: t("approvalSteps.senderEmail.title"),
    });
  };

  return (
    <Box mb="25px" mt="9px">
      <SwitchField
        label={t("approvalSteps.enableLabel", { recipient })}
        value={isEnabled}
        onChange={onEnableNotification}
        style={{ marginBottom: isEnabled ? 10 : 0 }}
        description={
          !isEnabled && t("approvalSteps.senderNotification.enableDescription")
        }
      />
      {isEnabled && (
        <Box padding="8px 16px 16px">
          <FormFields>
            <Field>
              <TextField
                name="title"
                variant="outlined"
                label={t("approvalSteps.title", { recipient })}
                value={senderNotification.title}
                onChange={handleNotificationText}
                error={titleError || titleMaxLength}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.TITLE }}
              />
              <ErrorMessage
                isError={titleError}
                wording={t("approvalSteps.fieldRequired")}
              />
              <ErrorMessage
                isError={titleMaxLength}
                wording={t("approvalSteps.fieldMaxLength", {
                  max: NOTIFICATION_LIMITS.TITLE,
                })}
              />
            </Field>
            <Field>
              <Typography
                variant="caption"
                color={bodyError ? "error" : "textSecondary"}
              >
                {t("approvalSteps.bodyText", { recipient })}
              </Typography>
              <AutocompleteSearchInput
                name="bodyText"
                multiple
                rows={5}
                fullWidth
                variables={variables}
                error={bodyError || bodyMaxLength}
                maxLength={NOTIFICATION_LIMITS.BODY_TEXT}
                value={replaceIdsWithLabels(senderNotification.bodyText)}
                onChange={handleNotificationBody}
              />
              <ErrorMessage
                isError={bodyError}
                wording={t("approvalSteps.fieldRequired")}
              />
              <ErrorMessage
                isError={bodyMaxLength}
                wording={t("approvalSteps.fieldMaxLength", {
                  max: NOTIFICATION_LIMITS.BODY_TEXT,
                })}
              />
            </Field>
            <SwitchField
              name="accessLink"
              label={t("approvalSteps.accessLink", { recipient })}
              description={t("approvalSteps.accessLinkDesc")}
              value={senderNotification.accessLink}
              onChange={handleNotificationLinks}
            />
            {senderNotification.accessLink && (
              <Field>
                <TextField
                  name="accessLinkLabel"
                  variant="outlined"
                  label={t("approvalSteps.accessLinkLabel")}
                  value={senderNotification.accessLinkLabel}
                  onChange={handleNotificationText}
                  error={accessLinkError}
                  inputProps={{ maxLength: NOTIFICATION_LIMITS.CTA_LABEL }}
                />
                <ErrorMessage
                  isError={accessLinkError}
                  wording={t("approvalSteps.fieldRequired")}
                />
              </Field>
            )}

            <SwitchField
              name="cancelLink"
              label={t("approvalSteps.cancelLink", { recipient })}
              description={t("approvalSteps.cancelLinkDesc")}
              value={senderNotification.cancelLink}
              onChange={handleNotificationLinks}
            />
            {senderNotification.cancelLink && (
              <Field>
                <TextField
                  name="cancelLinkLabel"
                  variant="outlined"
                  label={t("approvalSteps.cancelLinkLabel")}
                  value={senderNotification.cancelLinkLabel}
                  onChange={handleNotificationText}
                  error={cancelLinkError}
                  inputProps={{ maxLength: NOTIFICATION_LIMITS.CTA_LABEL }}
                />
                <ErrorMessage
                  isError={cancelLinkError}
                  wording={t("approvalSteps.fieldRequired")}
                />
              </Field>
            )}
          </FormFields>
        </Box>
      )}
    </Box>
  );
}
